import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowDownLeft, ArrowUpRight, ShoppingBag, Clock } from 'lucide-react';
import { useGDLData } from './useGDLData';
import { PinPad, SuccessScreen, EmptyState } from './GDLComponents';
import AdamuTimeline from './AdamuTimeline';

const API = process.env.REACT_APP_BACKEND_URL || '';
const BANKS = ['GTBank', 'Access Bank', 'UBA', 'First Bank', 'Zenith Bank', 'Fidelity Bank', 'Stanbic IBTC', 'Sterling Bank', 'Wema Bank', 'FCMB'];

export default function GDLWallet() {
  const nav = useNavigate();
  const { ledger, csId, refresh } = useGDLData();
  const [tab, setTab] = useState('transactions');
  const [filter, setFilter] = useState('all');
  const [sendStep, setSendStep] = useState(1);
  const [sendBank, setSendBank] = useState('');
  const [sendAcct, setSendAcct] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendResult, setSendResult] = useState(null);
  const [adamuOpen, setAdamuOpen] = useState(false);

  if (!csId) { nav('/demo/gdl/login'); return null; }

  const filtered = filter === 'all' ? (ledger || [])
    : filter === 'credits' ? (ledger || []).filter(t => t.amount >= 0)
    : filter === 'debits' ? (ledger || []).filter(t => t.amount < 0)
    : (ledger || []).filter(t => t.type === 'vendor' || t.type === 'bill');

  const handlePinComplete = async (pin) => {
    if (pin !== '123456') { setSendStep(1); return; }
    try {
      const { data } = await axios.post(`${API}/api/demo/gdl/bills/${csId}`, {
        category: 'Transfer', provider: sendBank, identifier: sendAcct, amount: parseFloat(sendAmount),
      }, { withCredentials: true });
      setSendResult(data); setSendStep(4); refresh();
    } catch (e) { setSendStep(1); }
  };

  const resetSend = () => { setSendStep(1); setSendBank(''); setSendAcct(''); setSendAmount(''); setSendResult(null); };

  return (
    <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Adamu timeline trigger */}
      <button onClick={() => setAdamuOpen(true)} data-testid="open-adamu-timeline" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(176,49,64,0.08), rgba(176,49,64,0.04))',
        border: '1px solid rgba(176,49,64,0.15)',
        cursor: 'pointer', textAlign: 'left',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg, #B03140, #8E2233)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Clock size={16} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2D0A10' }}>
            Watch what just happened
          </div>
          <div style={{ fontSize: 11, color: '#8E2233', marginTop: 1 }}>
            8 seconds — salary → ring-fence → audit → notification
          </div>
        </div>
      </button>

      <AdamuTimeline csId={csId} open={adamuOpen} onClose={() => setAdamuOpen(false)} />
      {/* Tab Bar */}
      <div style={{ display: 'flex', background: 'rgba(176,49,64,0.06)', borderRadius: 14, padding: 4 }}>
        {['transactions', 'send'].map(t => (
          <button key={t} onClick={() => { setTab(t); if (t === 'send') resetSend(); }}
            data-testid={`wallet-tab-${t}`}
            style={{
              flex: 1, padding: '11px 0', border: 'none', fontSize: 14, fontWeight: 600,
              background: tab === t ? '#fff' : 'transparent', color: tab === t ? '#B03140' : '#A07078',
              cursor: 'pointer', borderRadius: 10, transition: 'all 0.2s ease',
              boxShadow: tab === t ? '0 2px 8px rgba(176,49,64,0.08)' : 'none',
            }}>
            {t === 'transactions' ? 'Transactions' : 'Send Money'}
          </button>
        ))}
      </div>

      {tab === 'transactions' && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['all', 'credits', 'debits', 'vendors'].map(f => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                style={{
                  padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600,
                  border: 'none', background: filter === f ? '#B03140' : 'rgba(176,49,64,0.06)',
                  color: filter === f ? '#fff' : '#8E2233', cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          {filtered.length === 0 && <EmptyState icon={Search} title="No transactions" body="Your transactions will appear here" />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(tx => {
              const isCredit = tx.amount >= 0;
              const isVendor = tx.type === 'vendor' || tx.type === 'bill';
              const iconBg = isVendor ? 'rgba(176,49,64,0.08)' : isCredit ? '#ECFDF5' : '#FEF2F2';
              const iconColor = isVendor ? '#B03140' : isCredit ? '#10B981' : '#EF4444';
              const Icon = isVendor ? ShoppingBag : isCredit ? ArrowDownLeft : ArrowUpRight;
              return (
                <div key={tx.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  background: '#fff', borderRadius: 16,
                  boxShadow: '0 1px 6px rgba(176,49,64,0.04)',
                }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={16} color={iconColor} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#2D0A10', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description || tx.type}</div>
                    <div style={{ fontSize: 11, color: '#A07078', marginTop: 1 }}>{new Date(tx.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: isCredit ? '#10B981' : '#2D0A10', flexShrink: 0 }}>
                    {isCredit ? '+' : '-'}{'\u20A6'}{Math.abs(tx.amount).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'send' && (
        <div style={{ background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 12px rgba(176,49,64,0.06)' }}>
          {sendStep === 1 && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2D0A10', marginBottom: 16 }}>Send Money</div>
              <label style={lbl}>Select Bank</label>
              <select value={sendBank} onChange={e => setSendBank(e.target.value)} style={inp} data-testid="send-bank">
                <option value="">Choose a bank</option>
                {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <label style={{ ...lbl, marginTop: 16 }}>Account Number</label>
              <input value={sendAcct} onChange={e => setSendAcct(e.target.value)} style={inp} placeholder="10-digit account number" maxLength={10} data-testid="send-acct" />
              <label style={{ ...lbl, marginTop: 16 }}>Amount ({'\u20A6'})</label>
              <input type="number" value={sendAmount} onChange={e => setSendAmount(e.target.value)} style={inp} placeholder="0.00" data-testid="send-amount" />
              <button onClick={() => setSendStep(2)} disabled={!sendBank || sendAcct.length < 10 || !sendAmount} data-testid="send-continue"
                style={{ ...btnP, marginTop: 20, opacity: (!sendBank || sendAcct.length < 10 || !sendAmount) ? 0.5 : 1 }}>
                Continue
              </button>
            </div>
          )}
          {sendStep === 2 && (
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2D0A10', marginBottom: 16 }}>Confirm Transfer</div>
              <div style={{ background: 'rgba(176,49,64,0.04)', borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#A07078', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recipient</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#2D0A10', marginTop: 4 }}>Account {sendAcct} {'\u2014'} {sendBank}</div>
              </div>
              <div style={{ background: 'rgba(176,49,64,0.04)', borderRadius: 14, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#A07078', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Amount</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#B03140', marginTop: 4 }}>{'\u20A6'}{parseFloat(sendAmount || 0).toLocaleString()}</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setSendStep(1)} style={btnS}>Back</button>
                <button onClick={() => setSendStep(3)} data-testid="send-confirm" style={btnP}>Confirm</button>
              </div>
            </div>
          )}
          {sendStep === 3 && <PinPad onComplete={handlePinComplete} title="Enter Transaction PIN" />}
          {sendStep === 4 && (
            <SuccessScreen title="Transfer Successful"
              subtitle={`${'\u20A6'}${parseFloat(sendAmount || 0).toLocaleString()} sent to ${sendBank}`}
              reference={sendResult?.reference || 'TXN-' + Date.now()}
              onDone={() => { resetSend(); setTab('transactions'); refresh(); }} />
          )}
        </div>
      )}
    </div>
  );
}

const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#8E2233', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };
const inp = { width: '100%', padding: '14px 16px', border: '1.5px solid rgba(176,49,64,0.12)', borderRadius: 12, fontSize: 15, outline: 'none', boxSizing: 'border-box', background: 'rgba(176,49,64,0.03)', color: '#2D0A10' };
const btnP = { flex: 1, padding: '15px', background: '#B03140', color: '#fff', border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 16px rgba(176,49,64,0.2)' };
const btnS = { flex: 1, padding: '15px', background: 'rgba(176,49,64,0.06)', color: '#B03140', border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: 'pointer' };
